import React, { useState, useRef, useEffect } from 'react';
import { Search, Loader2, Settings, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from '@google-recaptcha/react';
import { removeGoogleReCaptchaScript, injectGoogleReCaptchaScript, checkGoogleReCaptchaInjected } from '@google-recaptcha/core';
import { getAuth, signInAnonymously, onAuthStateChanged, type User } from "firebase/auth";
import { AuditResult } from './types';
import { analyzeDomain } from './services/analyzer';
import { verifyReCaptcha } from './services/captchaVerify';
import { decrypt } from './services/cryptoService';
import { ensureAnonymousAuth, getIdToken } from './services/firebase';
import { useTheme } from './context/ThemeContext';
import { ThemeToggle } from './components/ThemeToggle';
// import { Dashboard } from './components/Dashboard';
const Dashboard = React.lazy(() => 
  import('./components/Dashboard').then(module => ({ default: module.Dashboard }))
);

const sanitizeDomainInput = (value: string): string => {
  let cleaned = value.trim().toLowerCase();

  // If it's a URL or contains protocol/www
  if (/^(https?:\/\/)?(www\.)?[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/.test(cleaned) || cleaned.includes('://') || cleaned.startsWith('www.')) {
    try {
      let urlString = cleaned;
      if (!/^https?:\/\//i.test(urlString)) {
        urlString = 'http://' + urlString;
      }
      const url = new URL(urlString);
      cleaned = url.hostname;
    } catch (e) {
      // Fallback simple split if URL parser fails
      cleaned = cleaned.replace(/^(https?:)?\/\//i, '');
      cleaned = cleaned.split('/')[0].split('?')[0].split(':')[0];
    }
  }

  // If it's an email address
  if (cleaned.includes('@')) {
    const parts = cleaned.split('@');
    const domainPart = parts[parts.length - 1];
    // Only sanitize if the domain part actually looks like a domain (contains a dot) or is a complete typing
    if (domainPart.includes('.') || domainPart.length > 3) {
      cleaned = domainPart;
    }
  }

  // Remove trailing slashes if any are left
  cleaned = cleaned.replace(/\/$/, '');

  return cleaned;
};

type AppWithChildrenProps = {
  children?: React.ReactNode
};
type AppSkeletonProps = {
  message?: string,
  isError?: boolean
}

const AppSkeleton: React.FC<AppSkeletonProps> = ({message = "Loading the Application...", isError = false}) => {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-800 text-slate-900 dark:text-white transition-colors duration-300 relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-lg">
        <AppHeader /> 
        <div className="bg-white dark:bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl dark:shadow-2xl border border-slate-200 dark:border-white/10 transition-all">
          <div className="space-y-6 max-w-full animate-pulse">
            <div
              className="block h-2 mb-2 font-sans text-base antialiased font-light leading-relaxed bg-gray-300 rounded-full text-inherit w-72">
              &nbsp;
            </div>
            <div className="relative group">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <Search className={`h-5 w-5 transition-colors ${isError ? 'text-red-400' : 'text-slate-400 group-focus-within:text-brand-500'}`} />
              </div>
              <input
                type="text"
                className={`block w-full pl-11 pr-4 py-4 bg-slate-50 dark:bg-slate-800/50 border rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:outline-none transition-all border-slate-200 dark:border-slate-600 focus:border-transparent focus:ring-brand-500`}
                placeholder={message}
                unselectable={"on"}
                disabled={true}
              />
            </div>
            <div
              className="block w-56 h-3 mb-4 font-sans text-5xl antialiased font-semibold leading-tight tracking-normal bg-gray-300 rounded-full text-inherit">
              &nbsp;
            </div>
            <button
              disabled={true}
            className={`
              block w-full h-3 mb-4 font-sans text-5xl antialiased font-semibold leading-tight tracking-normal bg-gray-300 rounded-full text-inherit 
              py-7 px-6 font-semibold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2 
              'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed shadow-none opacity-50
            `}
          >
          </button>
          </div>

          {/* Submission Error (API Failures) */}
          {/* {submitError && (
            <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg text-red-600 dark:text-red-400 text-sm text-center animate-in fade-in">
              {submitError}
            </div>
          )} */}            
        </div>
        <AppFooter />
      </div>
    </div>
  );
}

const AppHeader: React.FC = () => {
  return (
    <div className="text-center mb-10">
      {/* Logo handling for both modes */}
      <img
        src="sentinel-logo.png"
        alt="Koent Domain Sentinel"
        className="h-24 w-auto mx-auto mb-4 object-contain dark:hidden"
      />
      <img
        src="sentinel-logo-white.png"
        alt="Koent Domain Sentinel"
        className="h-24 w-auto mx-auto mb-4 object-contain hidden dark:block"
      />
      <p className="text-slate-600 dark:text-slate-400 text-lg mt-4">
        Enterprise-grade email security audit. Verify SPF, DMARC, and DKIM to protect your brand reputation.
      </p>
    </div>
  );
}

const AppFooter: React.FC = () => {
  return (
    <div className="mt-8 text-center">
      <p className="text-slate-500 text-sm">
        Analyzes public DNS records. No private access required.
        <br />
        Koent Domain Sentinel Security Audit Tool &copy; 2025
        <br />
        Built with ❤️ by <a href="https://koent.com" target="_blank" rel="noopener noreferrer" className="hover:text-brand-500 transition-colors">Koent</a>
        <br />
        Koent is a leading Software House based in Italy.
        <br />
        Koent S.r.l. - VAT: IT12020000969 - Piazza IV Novembre 4, 20124 Milano (MI), Italy
      </p>
    </div>
  );
}

const App: React.FC = () => {
  const [authLoading, setAuthLoading] = useState(true);
  const [user, setUser] = useState<User|null>(null);
  const [captchaLoading, setCaptchaLoading] = useState(false);
  const [captchaLoaded, setCaptchaLoaded] = useState(true);
  // const [captchaLoading, setCaptchaLoading] = useState(true);
  // const [captchaLoaded, setCaptchaLoaded] = useState(false);

  const [domain, setDomain] = useState('');
  const [selector, setSelector] = useState('');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AuditResult | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isTouched, setIsTouched] = useState(false);
  const googleReCaptcha = useGoogleReCaptcha();

  // useEffect(() => {
  //   setCaptchaLoaded(!!googleReCaptcha.instance);
  //   setCaptchaLoading(!!googleReCaptcha.isLoading);
  //   console.log(googleReCaptcha, captchaLoaded, captchaLoading);
  // }, [
  //   googleReCaptcha.instance,
  //   googleReCaptcha.isLoading,
  //   googleReCaptcha.language,
  //   googleReCaptcha.siteKey,
  //   googleReCaptcha.theme,
  //   googleReCaptcha.executeV3,
  //   googleReCaptcha.executeV2Invisible,
  //   googleReCaptcha.getResponse,
  //   googleReCaptcha.render,
  //   googleReCaptcha.reset,
  //   captchaLoading,
  //   captchaLoaded
  // ])
  
  useEffect(() => {
    const auth = getAuth();

    // Imposta l'ascoltatore sullo stato di Firebase
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        // C'è un utente (anonimo o registrato)
        setUser(currentUser);
        setAuthLoading(false);
      } else {
        // Nessun utente loggato: lanciamo la funzione async per il login anonimo
        try {
          // Se usi una tua utility, sostituisci la riga sotto con: await myAsyncAnonLogin();
          await ensureAnonymousAuth();
          
          // NOTA: Non serve chiamare setUser o setLoading(false) qui.
          // signInAnonymously aggiornerà lo stato di Firebase, il che 
          // farà scattare nuovamente onAuthStateChanged con il nuovo utente!
        } catch (error) {
          console.error("Errore durante il login anonimo:", error);
          // Gestisci l'errore sbloccando l'app o mostrando un messaggio
          setAuthLoading(false); 
        }
      }
    });

    // Cleanup dell'ascoltatore quando il componente viene smontato
    return () => unsubscribe();
  }, []);

  // Handle query strings on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const encryptedDn = params.get('dn');
    const encryptedDkim = params.get('dkim');
    const encryptedRd = params.get('rd');

    if (encryptedDn) {
      const decryptedDn = decrypt(encryptedDn);
      
      // If dn is not specified or incorrect, go back to homepage behavior (do nothing, stay on home)
      if (decryptedDn && domainRegex.test(decryptedDn)) {
        setDomain(decryptedDn);
        
        // Handle DKIM if provided
        let selectorToUse = '';
        if (encryptedDkim) {
          const decryptedDkim = decrypt(encryptedDkim);
          if (decryptedDkim) {
            selectorToUse = decryptedDkim;
            setSelector(decryptedDkim);
          }
        }

        // Trigger analysis automatically
        handleDeepLinkAnalysis(decryptedDn, selectorToUse);
      } else {
        // Clear parameters if invalid dn
        window.history.replaceState({}, '', window.location.pathname);
      }
    }
  }, []);

  const handleDeepLinkAnalysis = async (targetDomain: string, targetSelector?: string) => {
    setLoading(true);
    setSubmitError(null);
    try {
      const data = await analyzeDomain(targetDomain, targetSelector || undefined);
      setResult(data);

      // Track analysis event from deep link
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'analyze_domain', {
          domain: targetDomain,
          has_selector: !!targetSelector,
          source: 'deep_link'
        });
      }
    } catch (err) {
      setSubmitError("Failed to analyze domain from link. Please try again manually.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Regex for domain validation:
  // 1. Starts with alphanumeric
  // 2. Can contain hyphens in middle
  // 3. Must have a TLD (e.g. .com) of at least 2 chars
  const domainRegex = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z]{2,})+$/;

  const isValidDomain = domainRegex.test(domain);
  const showValidationError = isTouched && domain.length > 0 && !isValidDomain;
  const isButtonDisabled = !domain || !isValidDomain || loading;

  const verifyReCaptchaWithBackend = async (action: string) => {
    if (!googleReCaptcha.executeV3) {
      console.error("Execute recaptcha not available");
      throw new Error("Execute recaptcha not available");
    }
    try {
      const token = await googleReCaptcha.executeV3(action);
      // your request with token
      const response = await verifyReCaptcha(token, action);
      console.log(response);
      if (!response.score) {
        console.warn("verifyReCaptchaWithBackend failed", response);
        throw new Error(response.error??response.errorText);
      }
      return response.score;
    } catch (err) {
     console.error("verifyReCaptchaWithBackend failed", err);
     throw new Error("verifyReCaptchaWithBackend failed");
    }
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (isButtonDisabled) return;

    setLoading(true);
    setSubmitError(null);

    // try {
    //   const score = await verifyReCaptchaWithBackend("SUBMIT");
    //   if (!score) {
    //     throw new Error("reCAPTCHA verification failed");
    //   }
      
    //   if (parseFloat(score.toString()) <= 0.3) {
    //     throw new Error("reCAPTCHA verification failed. reCAPTCHA is scoring you as a bot.")
    //   }
    // } catch (err) {
    //   setLoading(false);
    //   setResult(null);
    //   setSubmitError((err as any).message);
    //   console.error(err);
    //   googleReCaptcha.instance?.reset();
    //   return;
    // }
    try {
      const data = await analyzeDomain(domain, selector || undefined);
      setResult(data);

      // Track analysis event
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'analyze_domain', {
          domain: domain,
          has_selector: !!selector,
          source: 'manual'
        });
      }
    } catch (err) {
      setSubmitError("Failed to analyze domain. Please verify the domain exists and try again.");
      console.error(err);
      googleReCaptcha.instance?.reset();
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const sanitized = sanitizeDomainInput(e.target.value);
    setDomain(sanitized);
    if (!isTouched) setIsTouched(true);
    // Clear general submit errors when user types
    if (submitError) setSubmitError(null);
  };

  const reset = () => {
    setResult(null);

    // Track reset event
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'reset_analysis', {});
    }
    // Keep the domain populated for easier re-testing, but reset errors
    setSubmitError(null);
    googleReCaptcha.instance?.reset();
  };

  // Finché non siamo sicuri di avere un utente, mostriamo un loader
  if (authLoading) {
    return (
      <AppSkeleton message={"Logging you into the Application..."} />
    );
  }
  if (!authLoading && !user) {
    return (
      <AppSkeleton message={"Error connecting with the backend system."} />
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-4 md:p-8 transition-colors duration-300">
        <div className="absolute top-4 right-4 z-50 print:hidden">
          <ThemeToggle />
        </div>
        <Dashboard result={result} onReset={reset} />
      </div>
    );
  }
  
  if (!captchaLoaded && captchaLoading) {
    return (
      <AppSkeleton message={"Loading ReCaptcha Application..."} />
    );
  }
  if (!captchaLoaded && !captchaLoading) { 
    return (
      <AppSkeleton message={"Error Loading ReCaptcha Application..."} isError={true} />
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 bg-slate-50 dark:bg-gradient-to-br dark:from-slate-900 dark:to-slate-800 text-slate-900 dark:text-white transition-colors duration-300 relative">
      <div className="absolute top-4 right-4">
        <ThemeToggle />
      </div>
      <div className="w-full max-w-lg">
        <AppHeader /> 
        <div className="bg-white dark:bg-white/10 backdrop-blur-md p-8 rounded-2xl shadow-xl dark:shadow-2xl border border-slate-200 dark:border-white/10 transition-all">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="domain" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                Target Domain
              </label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Search className={`h-5 w-5 transition-colors ${showValidationError ? 'text-red-400' : 'text-slate-400 group-focus-within:text-brand-500'}`} />
                </div>
                <input
                  type="text"
                  id="domain"
                  className={`block w-full pl-11 pr-4 py-4 bg-slate-50 dark:bg-slate-800/50 border rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:outline-none transition-all ${showValidationError
                    ? 'border-red-500/50 focus:border-red-500 focus:ring-red-500/20'
                    : 'border-slate-200 dark:border-slate-600 focus:border-transparent focus:ring-brand-500'
                    }`}
                  placeholder="company.com"
                  value={domain}
                  onChange={handleInputChange}
                  onBlur={() => setIsTouched(true)}
                />
              </div>

              {/* Real-time Validation Error */}
              <div className={`overflow-hidden transition-all duration-300 ease-in-out ${showValidationError ? 'max-h-12 opacity-100 mt-2' : 'max-h-0 opacity-0'}`}>
                <div className="flex items-center gap-2 text-red-500 dark:text-red-400 text-sm">
                  <AlertCircle size={14} />
                  <span>Please enter a valid domain name (e.g., google.com)</span>
                </div>
              </div>
            </div>

            {/* Advanced Options Toggle */}
            <div>
              <button
                type="button"
                onClick={() => setShowAdvanced(!showAdvanced)}
                className="flex items-center gap-2 text-xs font-medium text-slate-500 dark:text-slate-400 hover:text-brand-600 dark:hover:text-brand-400 transition-colors focus:outline-none"
              >
                <Settings size={14} />
                Advanced Options
                {showAdvanced ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              {showAdvanced && (
                <div className="mt-3 animate-in fade-in slide-in-from-top-2 duration-200">
                  <label htmlFor="selector" className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">
                    Custom DKIM Selector (Optional)
                  </label>
                  <input
                    type="text"
                    id="selector"
                    className="block w-full px-4 py-3 bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-600 rounded-xl text-slate-900 dark:text-white placeholder-slate-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all text-sm"
                    placeholder="e.g., google, s1, 20230601"
                    value={selector}
                    onChange={(e) => setSelector(e.target.value)}
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    We automatically check common selectors. Enter a specific one if you know it.
                  </p>
                </div>
              )}
            </div>

            {/* Submission Error (API Failures) */}
            {submitError && (
              <div className="p-3 bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/20 rounded-lg text-red-600 dark:text-red-400 text-sm text-center animate-in fade-in">
                {submitError}
              </div>
            )}

            <button
              type="submit"
              disabled={isButtonDisabled}
              className={`w-full py-4 px-6 font-semibold rounded-xl shadow-lg transition-all flex items-center justify-center gap-2
                  ${isButtonDisabled
                  ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed shadow-none opacity-50'
                  : 'bg-brand-600 hover:bg-brand-500 text-white shadow-brand-500/20'
                }`}
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" /> Analyzing DNS...
                </>
              ) : (
                "Analyze Security"
              )}
            </button>
          </form>
        </div>

        <AppFooter />
      </div>
    </div>
  );
};

const AppWithReCaptcha: React.FC = () => {
  const { theme } = useTheme();
  const recaptchaTheme = theme === 'system'
    ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
    : theme;  
  return (
    <GoogleReCaptchaProvider 
      language={'en'}
      type={'v3'}
      host={'google.com'}
      // scriptProps={undefined}
      explicit={{badge:"bottomleft",}}
      isEnterprise={true}
      siteKey={'6LeJeIAtAAAAAPd_ZJa2YcnUTHApEj5VI9DY3HCv'}
      //siteKey={'import.meta.env.VITE_RECAPTCHA_SITE_KEY || "RECAPTCHA_SITE_KEY"'}
      theme={recaptchaTheme}
      onLoad={async (instance) => {
        console.log("GoogleReCaptchaProvider loaded")
      }}
      onError={async () => {
        console.error("GoogleReCaptchaProvider onError");
        removeGoogleReCaptchaScript();
        injectGoogleReCaptchaScript({
          onload:()=>{},
          appendTo: "head",
          isEnterprise: true,
          host: "google.com",
          render: '6LeJeIAtAAAAAPd_ZJa2YcnUTHApEj5VI9DY3HCv',
          //render: import.meta.env.VITE_RECAPTCHA_SITE_KEY,
          hl: "en",
        });
        checkGoogleReCaptchaInjected();
      }}
    >
      <App/>
    </GoogleReCaptchaProvider>
  );
}

const AppToExport: React.FC = () => {
  return (
    // <AppWithReCaptcha/>
    <App/>
  );
}

export default AppToExport;
